/**
 * Stock movement ledger page.
 *
 * Tabs:
 *   1. Movement Ledger — paginated grid driven by `/inventory/ledger/`
 *   2. Stock-out & SLA — `/inventory/stockouts/` + aggregate KPIs
 *   3. Damage         — `/inventory/damage/` + `/damage/movements/`
 *   4. KPI overview   — `/inventory/ledger/summary/`
 *
 * Date range and store come from the global filter state (Topbar). Local
 * filters (movement type, condition, search, brand/category/etc.) are kept
 * in component state — they're per-view, not session-wide.
 */
import { useMemo, useState } from 'react';
import { Boxes, Download, ShieldAlert, TrendingDown, TruckIcon } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScroll,
} from '@/components/ui/Table';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { KPICard } from '@/components/charts/KPICard';
import { TaxonomyRail } from '@/components/inventory/TaxonomyRail';
import { BarChart } from '@/components/charts/BarChart';
import { useFilters, rangeISO } from '@/state/filters';
import {
  downloadStockLedgerCsv,
  useDamageMovements,
  useDamageSummary,
  useInventoryTaxonomy,
  useStockLedger,
  useStockLedgerSummary,
  useStockoutAggregate,
  useStockouts,
  type LedgerFilters,
  type StockoutFilters,
} from '@/api/bi';
import type {
  LedgerCondition,
  LedgerFinancials,
  LedgerMovementType,
  LedgerRow,
  PaginatedLedgerResponse,
  StockoutRow,
} from '@/api/bi/types';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useAccess } from '@/features/AccessProvider';

const PAGE_SIZE = 50;

const MOVEMENT_LABEL: Record<LedgerMovementType, string> = {
  GRN_RECEIPT: 'GRN Receipt',
  TRANSFER_OUT: 'Transfer Out',
  TRANSFER_IN: 'Transfer In',
  SALE: 'Sale',
  SALE_CANCEL: 'Sale Cancel',
  CUSTOMER_RETURN: 'Customer Return',
  DAMAGE_DETECTED: 'Damage Detected',
  DAMAGE_TRANSFER_OUT: 'Damage Transfer Out',
  DAMAGE_TRANSFER_IN: 'Damage Transfer In',
  DAMAGE_TO_VENDOR: 'Damage to Vendor',
};

const MOVEMENT_TONE: Record<LedgerMovementType, 'success' | 'danger' | 'warning' | 'neutral' | 'info'> = {
  GRN_RECEIPT: 'success',
  TRANSFER_IN: 'info',
  TRANSFER_OUT: 'warning',
  SALE: 'neutral',
  SALE_CANCEL: 'info',
  CUSTOMER_RETURN: 'info',
  DAMAGE_DETECTED: 'danger',
  DAMAGE_TRANSFER_OUT: 'danger',
  DAMAGE_TRANSFER_IN: 'warning',
  DAMAGE_TO_VENDOR: 'danger',
};

const MOVEMENT_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All movements' },
  { value: 'GRN_RECEIPT', label: 'GRN Receipt' },
  { value: 'TRANSFER_IN', label: 'Transfer In' },
  { value: 'TRANSFER_OUT', label: 'Transfer Out' },
  { value: 'SALE', label: 'Sale' },
  { value: 'SALE_CANCEL', label: 'Sale Cancel' },
  { value: 'CUSTOMER_RETURN', label: 'Customer Return' },
  { value: 'damage', label: 'Damage (all)' },
];

export function InventoryLedgerPage() {
  const { range, storeId, timeRangeMode } = useFilters();
  const { can } = useAccess();
  const canDamageTab = can('inventory.damage');
  const canKpiOverviewTab = can('inventory.kpi-overview');
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);

  const [movementType, setMovementType] = useState<string>('all');
  const [condition, setCondition] = useState<LedgerCondition | 'all'>('all');
  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [categoryGroupIds, setCategoryGroupIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [subcategoryIds, setSubcategoryIds] = useState<number[]>([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [stockoutPage, setStockoutPage] = useState(1);
  const [damagePage, setDamagePage] = useState(1);
  const [currentlyOutOnly, setCurrentlyOutOnly] = useState(false);

  const taxonomy = useInventoryTaxonomy();

  const baseFilters = { start: iso.start, end: iso.end, storeId };
  const taxonomyFilters = {
    vendorIds,
    brandIds,
    categoryGroupIds,
    categoryIds,
    subcategoryIds,
    q: q.trim() || undefined,
  };

  const resetPages = () => {
    setPage(1);
    setStockoutPage(1);
    setDamagePage(1);
  };

  const ledgerFilters: LedgerFilters = {
    ...baseFilters,
    ...taxonomyFilters,
    movementType: movementType === 'all'
      ? undefined
      : (movementType as LedgerFilters['movementType']),
    condition: condition === 'all' ? undefined : condition,
    page,
    pageSize: PAGE_SIZE,
  };

  const stockoutFilters: StockoutFilters = {
    ...baseFilters,
    ...taxonomyFilters,
    page: stockoutPage,
    pageSize: PAGE_SIZE,
    currentlyOutOnly,
  };

  const ledger = useStockLedger(ledgerFilters);
  const summary = useStockLedgerSummary({
    ...ledgerFilters,
    page: undefined,
    pageSize: undefined,
    enabled: canKpiOverviewTab,
  });
  const stockouts = useStockouts(stockoutFilters);
  const stockoutAgg = useStockoutAggregate({ ...stockoutFilters, page: undefined, pageSize: undefined });
  const damageSummary = useDamageSummary({
    ...baseFilters,
    ...taxonomyFilters,
    movementType: undefined,
    condition: undefined,
    enabled: canDamageTab,
  });
  const damageMovements = useDamageMovements({
    ...baseFilters,
    ...taxonomyFilters,
    page: damagePage,
    pageSize: PAGE_SIZE,
    enabled: canDamageTab,
  });

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Stock Ledger"
        description="Every stock-changing event with running balance per (product, store, condition). Use the date and store picker above to scope."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void downloadStockLedgerCsv(ledgerFilters)}
            aria-label="Export ledger to CSV"
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            Export CSV
          </Button>
        }
      />

      <TaxonomyRail
        taxonomy={taxonomy.data}
        taxonomyLoading={taxonomy.isLoading}
        vendorIds={vendorIds}
        onVendorIds={(v) => { setVendorIds(v); resetPages(); }}
        brandIds={brandIds}
        onBrandIds={(v) => { setBrandIds(v); resetPages(); }}
        categoryGroupIds={categoryGroupIds}
        onCategoryGroupIds={(v) => { setCategoryGroupIds(v); resetPages(); }}
        categoryIds={categoryIds}
        onCategoryIds={(v) => { setCategoryIds(v); resetPages(); }}
        subcategoryIds={subcategoryIds}
        onSubcategoryIds={(v) => { setSubcategoryIds(v); resetPages(); }}
        q={q}
        onQ={(v) => { setQ(v); resetPages(); }}
        onClear={() => {
          setVendorIds([]);
          setBrandIds([]);
          setCategoryGroupIds([]);
          setCategoryIds([]);
          setSubcategoryIds([]);
          setQ('');
          resetPages();
        }}
      />

      <Tabs defaultValue="ledger">
        <TabsList className="mb-4">
          <TabsTrigger value="ledger">Movement Ledger</TabsTrigger>
          <TabsTrigger value="sla">Stock-out & SLA</TabsTrigger>
          {canDamageTab ? <TabsTrigger value="damage">Damage</TabsTrigger> : null}
          {canKpiOverviewTab ? <TabsTrigger value="overview">KPI Overview</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="ledger" className="space-y-4 outline-none">
          <LedgerLocalRail
            movementType={movementType}
            onMovementType={(v) => {
              setMovementType(v);
              setPage(1);
            }}
            condition={condition}
            onCondition={(v) => {
              setCondition(v);
              setPage(1);
            }}
          />
          <Card>
            <CardHeader>
              <CardTitle>Movements</CardTitle>
              {ledger.data ? (
                <Badge tone="neutral">{fmt.number(ledger.data.total)} rows</Badge>
              ) : null}
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <LedgerTable
                data={ledger.data}
                isLoading={ledger.isLoading}
                isError={ledger.isError}
                onRetry={() => void ledger.refetch()}
                page={page}
                onPage={setPage}
              />
            </CardBody>
          </Card>
        </TabsContent>

        <TabsContent value="sla" className="space-y-4 outline-none">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard
              label="Stock-out events"
              value={stockoutAgg.data ? fmt.number(stockoutAgg.data.events_count) : '—'}
              loading={stockoutAgg.isLoading}
              hint="Crossings from balance > 0 to 0 in the GOOD partition"
            />
            <KPICard
              label="Currently out"
              value={stockoutAgg.data ? fmt.number(stockoutAgg.data.open_events) : '—'}
              loading={stockoutAgg.isLoading}
              goodWhenUp={false}
              hint="Events without a replenishment row"
            />
            <KPICard
              label="Mean TTR (h)"
              value={
                stockoutAgg.data?.mean_ttr_hours != null
                  ? fmt.number(stockoutAgg.data.mean_ttr_hours)
                  : '—'
              }
              loading={stockoutAgg.isLoading}
              goodWhenUp={false}
              hint="Time to recovery (hours), mean over closed events"
            />
            <KPICard
              label="P90 TTR (h)"
              value={
                stockoutAgg.data?.p90_ttr_hours != null
                  ? fmt.number(stockoutAgg.data.p90_ttr_hours)
                  : '—'
              }
              loading={stockoutAgg.isLoading}
              goodWhenUp={false}
              hint="90th percentile recovery time"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Stock-out events</CardTitle>
              <div className="flex items-center gap-2">
                <Badge
                  tone={currentlyOutOnly ? 'danger' : 'neutral'}
                  className="cursor-pointer select-none"
                  onClick={() => {
                    setCurrentlyOutOnly((v) => !v);
                    setStockoutPage(1);
                  }}
                  role="button"
                >
                  {currentlyOutOnly ? 'Currently out only' : 'All events'}
                </Badge>
              </div>
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <StockoutTable
                rows={stockouts.data?.rows ?? []}
                isLoading={stockouts.isLoading}
                isError={stockouts.isError}
                onRetry={() => void stockouts.refetch()}
                page={stockoutPage}
                onPage={setStockoutPage}
                total={stockouts.data?.total ?? 0}
              />
            </CardBody>
          </Card>

          {stockoutAgg.data?.top_offenders?.length ? (
            <Card>
              <CardHeader>
                <CardTitle>Top OOS offenders</CardTitle>
              </CardHeader>
              <CardBody>
                <ul className="space-y-1 text-sm">
                  {stockoutAgg.data.top_offenders.map((p) => (
                    <li
                      key={p.product_id}
                      className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
                    >
                      <span>{p.product_name}</span>
                      <Badge tone="danger">{fmt.number(p.events)} events</Badge>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}
        </TabsContent>

        {canDamageTab ? (
        <TabsContent value="damage" className="space-y-4 outline-none">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard
              label="On-hand · sub-stores"
              value={
                damageSummary.data
                  ? fmt.number(damageSummary.data.kpis.on_hand_sub_stores)
                  : '—'
              }
              loading={damageSummary.isLoading}
              goodWhenUp={false}
              hint="Damaged units sitting in sub-stores right now"
            />
            <KPICard
              label="On-hand · MAIN"
              value={
                damageSummary.data
                  ? fmt.number(damageSummary.data.kpis.on_hand_main)
                  : '—'
              }
              loading={damageSummary.isLoading}
              goodWhenUp={false}
              hint="Damaged units waiting at MAIN warehouse"
            />
            <KPICard
              label="Transferred to MAIN"
              value={
                damageSummary.data
                  ? fmt.number(damageSummary.data.kpis.transferred_to_main_window)
                  : '—'
              }
              loading={damageSummary.isLoading}
              hint="Damaged units moved to MAIN in this window"
            />
            <KPICard
              label="Returned to vendor"
              value={
                damageSummary.data
                  ? fmt.number(damageSummary.data.kpis.transferred_to_vendor_window)
                  : '—'
              }
              loading={damageSummary.isLoading}
              hint="DAMAGE_TO_VENDOR units in this window"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Damage on hand · per store</CardTitle>
                {damageSummary.data ? (
                  <Badge tone="neutral">
                    {fmt.npr(damageSummary.data.kpis.damage_value_estimate)} value
                  </Badge>
                ) : null}
              </CardHeader>
              <CardBody>
                {damageSummary.isLoading ? (
                  <Skeleton className="h-40 w-full" />
                ) : !damageSummary.data?.on_hand_per_store?.length ? (
                  <EmptyState icon={<Boxes className="h-5 w-5" />} title="No damaged stock on hand" />
                ) : (
                  <ul className="space-y-1 text-sm">
                    {damageSummary.data.on_hand_per_store.map((row) => (
                      <li
                        key={row.store_id}
                        className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
                      >
                        <span className="text-muted-foreground">Store #{row.store_id}</span>
                        <span className="kpi-num font-medium">{fmt.number(row.qty)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Damage movements · summary</CardTitle>
              </CardHeader>
              <CardBody>
                {damageSummary.isLoading ? (
                  <Skeleton className="h-40 w-full" />
                ) : !damageSummary.data?.movements_summary?.length ? (
                  <EmptyState icon={<ShieldAlert className="h-5 w-5" />} title="No damage activity" />
                ) : (
                  <BarChart
                    data={damageSummary.data.movements_summary.map((m) => ({
                      label: MOVEMENT_LABEL[m.movement_type] ?? m.movement_type,
                      qty: m.qty_in + m.qty_out,
                    }))}
                    xKey="label"
                    yKey="qty"
                    formatValue={(v) => fmt.number(v)}
                  />
                )}
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Damage movements</CardTitle>
              {damageMovements.data ? (
                <Badge tone="neutral">{fmt.number(damageMovements.data.total)} rows</Badge>
              ) : null}
            </CardHeader>
            <CardBody className="px-0 pb-0">
              <LedgerTable
                data={damageMovements.data}
                isLoading={damageMovements.isLoading}
                isError={damageMovements.isError}
                onRetry={() => void damageMovements.refetch()}
                page={damagePage}
                onPage={setDamagePage}
              />
            </CardBody>
          </Card>
        </TabsContent>
        ) : null}

        {canKpiOverviewTab ? (
        <TabsContent value="overview" className="space-y-4 outline-none">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KPICard
              label="Total in"
              value={summary.data ? fmt.number(summary.data.totals.qty_in) : '—'}
              loading={summary.isLoading}
            />
            <KPICard
              label="Total out"
              value={summary.data ? fmt.number(summary.data.totals.qty_out) : '—'}
              loading={summary.isLoading}
              goodWhenUp={false}
            />
            <KPICard
              label="Net movement"
              value={summary.data ? fmt.number(summary.data.totals.net) : '—'}
              loading={summary.isLoading}
            />
            <KPICard
              label="Ledger rows"
              value={summary.data ? fmt.number(summary.data.totals.rows) : '—'}
              loading={summary.isLoading}
            />
          </div>

          <FinancialsSection
            financials={summary.data?.financials}
            isLoading={summary.isLoading}
          />

          <Card>
            <CardHeader>
              <CardTitle>Movements by type · in/out</CardTitle>
            </CardHeader>
            <CardBody>
              {summary.isLoading ? (
                <Skeleton className="h-72 w-full" />
              ) : !summary.data?.by_movement_type?.length ? (
                <EmptyState icon={<TrendingDown className="h-5 w-5" />} title="No movements in window" />
              ) : (
                <BarChart
                  data={summary.data.by_movement_type.map((m) => ({
                    label: MOVEMENT_LABEL[m.movement_type] ?? m.movement_type,
                    delta: m.qty_in - m.qty_out,
                  }))}
                  xKey="label"
                  yKey="delta"
                  formatValue={(v) => fmt.number(v)}
                />
              )}
            </CardBody>
          </Card>
        </TabsContent>
        ) : null}
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------
// Financials (sales / purchase / profitability)
// ---------------------------------------------------------------------------

interface FinancialsSectionProps {
  financials: LedgerFinancials | undefined;
  isLoading: boolean;
}

function FinancialsSection({ financials, isLoading }: FinancialsSectionProps) {
  const f = financials;
  const marginLabel =
    f?.gross_margin_pct == null ? '—' : `${fmt.number(f.gross_margin_pct)}%`;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Sale qty"
          value={f ? fmt.number(f.sale_qty) : '—'}
          loading={isLoading}
          hint="Units sold (SALE rows). Cancellations & returns shown separately."
        />
        <KPICard
          label="Sale amount"
          value={f ? fmt.npr(f.sale_amount) : '—'}
          loading={isLoading}
          hint="Revenue from SALE rows (gross of cancellations/returns)."
        />
        <KPICard
          label="Purchase qty"
          value={f ? fmt.number(f.purchase_qty) : '—'}
          loading={isLoading}
          hint="Units received via accepted GRN cartons in this window."
        />
        <KPICard
          label="Purchase amount"
          value={f ? fmt.npr(f.purchase_amount) : '—'}
          loading={isLoading}
          hint="Cost of goods received (GRN price × units)."
        />
        <KPICard
          label="COGS (net)"
          value={f ? fmt.npr(f.net_cogs) : '—'}
          loading={isLoading}
          goodWhenUp={false}
          hint="Sale COGS minus cancellations and returns."
        />
        <KPICard
          label="Net sales"
          value={f ? fmt.npr(f.net_sale_amount) : '—'}
          loading={isLoading}
          hint="Sale amount minus cancellations and returns."
        />
        <KPICard
          label="Gross profit"
          value={f ? fmt.npr(f.gross_profit) : '—'}
          loading={isLoading}
          hint="Net sales − net COGS."
        />
        <KPICard
          label="Gross margin"
          value={marginLabel}
          loading={isLoading}
          hint="Gross profit ÷ net sales."
        />
      </div>

      {f && (f.sale_amount > 0 || f.purchase_amount > 0) ? (
        <Card>
          <CardHeader>
            <CardTitle>Sales · purchases · profit</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={[
                { label: 'Net sales', amount: f.net_sale_amount },
                { label: 'Net COGS', amount: f.net_cogs },
                { label: 'Gross profit', amount: f.gross_profit },
                { label: 'Purchases', amount: f.purchase_amount },
              ]}
              xKey="label"
              yKey="amount"
              formatValue={(v) => fmt.npr(v)}
            />
          </CardBody>
        </Card>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Filter rails
// ---------------------------------------------------------------------------

interface LedgerLocalRailProps {
  movementType: string;
  onMovementType: (v: string) => void;
  condition: LedgerCondition | 'all';
  onCondition: (v: LedgerCondition | 'all') => void;
}

function LedgerLocalRail({
  movementType, onMovementType,
  condition, onCondition,
}: LedgerLocalRailProps) {
  return (
    <Card>
      <CardBody className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground">Movement type</label>
          <Select value={movementType} onValueChange={onMovementType}>
            <SelectTrigger className="h-9 w-[200px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MOVEMENT_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground">Condition</label>
          <Select value={condition} onValueChange={(v) => onCondition(v as LedgerCondition | 'all')}>
            <SelectTrigger className="h-9 w-[160px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="GOOD">Good</SelectItem>
              <SelectItem value="DAMAGED">Damaged</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Ledger table
// ---------------------------------------------------------------------------

interface LedgerTableProps {
  data: PaginatedLedgerResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  page: number;
  onPage: (n: number) => void;
}

function LedgerTable({ data, isLoading, isError, onRetry, page, onPage }: LedgerTableProps) {
  if (isLoading) return <Skeleton className="m-5 h-48 w-[calc(100%-2.5rem)]" />;
  if (isError) return <div className="p-5"><ErrorState onRetry={onRetry} /></div>;
  if (!data || data.rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState icon={<TruckIcon className="h-5 w-5" />} title="No movements match these filters" />
      </div>
    );
  }
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <div>
      {/*
       * Bounded height so the header pins: a full page of movements runs well
       * past a screen, and the columns are near-identical numbers that mean
       * nothing once you have scrolled the labels away.
       */}
      <TableScroll maxHeight="65vh">
        <Table className="min-w-full">
          <TableHead>
            <TableRow>
              <TableHeaderCell stickyLeft>When</TableHeaderCell>
              <TableHeaderCell>Store</TableHeaderCell>
              <TableHeaderCell>Product</TableHeaderCell>
              <TableHeaderCell>Movement</TableHeaderCell>
              <TableHeaderCell>Cond.</TableHeaderCell>
              <TableHeaderCell className="text-right">Opening</TableHeaderCell>
              <TableHeaderCell className="text-right">In</TableHeaderCell>
              <TableHeaderCell className="text-right">Out</TableHeaderCell>
              <TableHeaderCell className="text-right">Balance</TableHeaderCell>
              <TableHeaderCell>Reference</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row) => (
              <LedgerRowView key={row.id} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableScroll>
      <Pager page={page} totalPages={totalPages} onPage={onPage} />
    </div>
  );
}

function LedgerRowView({ row }: { row: LedgerRow }) {
  return (
    <TableRow className="align-top">
      <TableCell stickyLeft className="whitespace-nowrap text-muted-foreground">
        {fmt.datetime(row.ts)}
      </TableCell>
      <TableCell>
        <span className="font-medium">{row.store.name}</span>
        <span className="ml-1 text-[10px] uppercase text-muted-foreground">{row.store.type}</span>
      </TableCell>
      <TableCell>
        <span className="font-medium">{row.product.name}</span>
        {row.product.subcategory_name ? (
          <div className="text-[11px] text-muted-foreground">
            {row.product.subcategory_name}
            {row.product.brand ? ` · ${row.product.brand}` : ''}
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <Badge tone={MOVEMENT_TONE[row.movement_type]}>{MOVEMENT_LABEL[row.movement_type]}</Badge>
      </TableCell>
      <TableCell>
        <Badge tone={row.condition === 'DAMAGED' ? 'danger' : 'neutral'}>{row.condition}</Badge>
      </TableCell>
      <TableCell className="kpi-num text-right text-muted-foreground">
        {fmt.number(row.opening)}
      </TableCell>
      <TableCell className="kpi-num text-right text-success">
        {row.qty_in ? `+${fmt.number(row.qty_in)}` : '—'}
      </TableCell>
      <TableCell className="kpi-num text-right text-danger">
        {row.qty_out ? `−${fmt.number(row.qty_out)}` : '—'}
      </TableCell>
      <TableCell className="kpi-num text-right font-semibold">{fmt.number(row.balance)}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{row.reference || '—'}</TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Stock-out table
// ---------------------------------------------------------------------------

function StockoutTable({
  rows,
  isLoading,
  isError,
  onRetry,
  page,
  total,
  onPage,
}: {
  rows: StockoutRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  page: number;
  total: number;
  onPage: (n: number) => void;
}) {
  if (isLoading) return <Skeleton className="m-5 h-40 w-[calc(100%-2.5rem)]" />;
  if (isError) return <div className="p-5"><ErrorState onRetry={onRetry} /></div>;
  if (rows.length === 0) {
    return (
      <div className="p-5">
        <EmptyState icon={<ShieldAlert className="h-5 w-5" />} title="No stock-out events" />
      </div>
    );
  }
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <div>
      <TableScroll maxHeight="65vh">
        <Table className="min-w-full">
          <TableHead>
            <TableRow>
              <TableHeaderCell stickyLeft>Stock-out at</TableHeaderCell>
              <TableHeaderCell>Store</TableHeaderCell>
              <TableHeaderCell>Product</TableHeaderCell>
              <TableHeaderCell>Replenished at</TableHeaderCell>
              <TableHeaderCell>By</TableHeaderCell>
              <TableHeaderCell className="text-right">TTR (h)</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${row.product_id}-${row.store_id}-${row.stock_out_at}`}>
                <TableCell stickyLeft className="whitespace-nowrap text-muted-foreground">
                  {fmt.datetime(row.stock_out_at)}
                </TableCell>
                <TableCell>{row.store_name}</TableCell>
                <TableCell>{row.product_name}</TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {row.replenished_at ? (
                    fmt.datetime(row.replenished_at)
                  ) : (
                    <Badge tone="danger">Currently out</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {row.replenished_by ? (
                    <Badge tone={MOVEMENT_TONE[row.replenished_by]}>
                      {MOVEMENT_LABEL[row.replenished_by]}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell
                  className={cn(
                    'kpi-num text-right',
                    row.ttr_hours == null
                      ? 'text-danger'
                      : row.ttr_hours > 48
                        ? 'text-warning-soft'
                        : 'text-foreground',
                  )}
                >
                  {row.ttr_hours == null ? '—' : fmt.number(row.ttr_hours)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableScroll>
      <Pager page={page} totalPages={totalPages} onPage={onPage} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pager
// ---------------------------------------------------------------------------

function Pager({
  page,
  totalPages,
  onPage,
}: {
  page: number;
  totalPages: number;
  onPage: (n: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
      <span className="text-muted-foreground">
        Page <span className="kpi-num">{page}</span> of <span className="kpi-num">{totalPages}</span>
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
